import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE, MaskLine, Reveal, SectionLabel } from "./motion";
import { Equalizer } from "./Hero";
import { IMAGES, LINKS } from "@/lib/site";
import { scrollToId } from "@/lib/lenis";

const CHAPTERS = [
  {
    n: "01",
    title: "L'inizio",
    text: "Ho iniziato con un telefono, un paio di cuffie rotte e una stanza troppo piccola per tutto quello che avevo da dire. Nessun produttore, nessun contratto: solo io, un microfono da due soldi e la testardaggine di chi non sa stare zitto.",
  },
  {
    n: "02",
    title: "Il suono",
    text: "Faccio pop, ma di quello vero, sporco, urbano. Melodie che ti restano in testa e parole che non passano dal filtro. Racconto le notti in giro, le cose che non si dicono ad alta voce e la fame di chi parte da zero.",
  },
  {
    n: "03",
    title: "Adesso",
    text: "I primi brani sono fuori su Spotify e su tutte le piattaforme, e ho un cassetto pieno di inediti che scalpitano. Sono all'inizio, lo so. Ma è proprio questo il bello: tu sei qui dal primo giorno. E il primo giorno non si scorda.",
  },
];

const STATS = [
  { v: "0", l: "etichette alle spalle" },
  { v: "100%", l: "indipendente, dal testo al mix" },
  { v: "∞", l: "notti passate in studio" },
];

const Portrait = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <div ref={ref} className="clip-frame relative aspect-[4/5] w-full overflow-hidden bg-ink-3" data-testid="bio-portrait">
      <motion.img style={{ y, scale: 1.2 }} src={IMAGES.bio} alt="SFUMO in studio" className="h-full w-full object-cover object-[70%_center] grayscale-[30%] contrast-[1.1]" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent" />
      <p className="absolute bottom-6 left-6 right-16 font-display text-xl font-bold uppercase leading-tight sm:text-2xl">
        “Sfumare vuol dire sparire piano. <span className="text-acid">Io ho scelto il contrario.</span>”
      </p>
    </div>
  );
};

const SpotifyCard = () => (
  <Reveal className="border border-line bg-ink-2 p-4 sm:p-6" data-testid="spotify-player-widget">
    <div className="mb-4 flex items-center justify-between">
      <span className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">
        <i className="fa-brands fa-spotify text-lg text-[#1DB954]" /> In ascolto su Spotify
      </span>
      <a href={LINKS.spotify} target="_blank" rel="noreferrer" data-testid="spotify-open-button" className="group flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] text-acid">
        Apri <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:rotate-45" />
      </a>
    </div>
    <iframe
      title="SFUMO su Spotify"
      src={LINKS.spotifyEmbed}
      width="100%"
      height="352"
      frameBorder="0"
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy"
      className="block rounded-xl"
    />
  </Reveal>
);

const Tile = ({ src, alt, className = "", imgClass = "", delay = 0, testid }) => (
  <Reveal delay={delay} className={`group relative overflow-hidden bg-ink-3 ${className}`} data-testid={testid}>
    <img src={src} alt={alt} loading="lazy" className={`h-full w-full object-cover transition-transform [transition-duration:1200ms] group-hover:scale-105 ${imgClass}`} />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
  </Reveal>
);

const StudioGallery = () => (
  <div className="mt-12 grid gap-6 lg:h-[640px] lg:grid-cols-12" data-testid="studio-gallery">
    <Tile src={IMAGES.studioCollage} alt="SFUMO al mixer e al microfono" className="aspect-[441/670] lg:col-span-3 lg:aspect-auto lg:h-full" imgClass="scale-[1.03]" testid="studio-photo-1" />
    <div className="grid gap-6 sm:grid-cols-2 lg:col-span-3 lg:h-full lg:grid-cols-1 lg:grid-rows-2">
      <Tile src={IMAGES.studioGuitar} alt="SFUMO alla chitarra" className="aspect-[4/3] lg:aspect-auto" delay={0.1} testid="studio-photo-2" />
      <Tile src={IMAGES.studioMic} alt="SFUMO registra la voce" className="aspect-[4/3] lg:aspect-auto" delay={0.2} testid="studio-photo-3" />
    </div>
    <Reveal delay={0.2} className="flex flex-col justify-between gap-8 border border-line bg-ink-2 p-8 sm:p-10 lg:col-span-6 lg:h-full">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-acid">Dietro le quinte</p>
      <div>
        <p className="font-display text-2xl font-bold uppercase leading-tight sm:text-3xl lg:text-4xl">
          Ogni pezzo nasce in una stanza, a notte fonda, con la porta chiusa e il volume troppo alto.
        </p>
        <p className="mt-6 max-w-md text-base leading-relaxed text-bone/70">
          Mixer, chitarra, un microfono e troppe tazze di caffè. Qui scrivo, registro e rifaccio tutto da capo finché non suona esattamente come lo sento dentro.
        </p>
      </div>
      <button onClick={() => scrollToId("crew")} data-testid="studio-crew-cta" className="self-start border-b border-acid pb-1 font-mono text-[11px] uppercase tracking-[0.2em] text-acid transition-opacity hover:opacity-70">
        Resta aggiornato sulle nuove uscite →
      </button>
    </Reveal>
  </div>
);

export const MusicBio = () => (
  <section id="musica" data-testid="music-bio-section" className="relative px-5 py-24 sm:px-8 lg:px-12 lg:py-36">
    <SectionLabel index="03">Musica & Bio</SectionLabel>
    <h2 className="mt-6 max-w-5xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
      <MaskLine>Nessuna etichetta.</MaskLine>
      <MaskLine delay={0.1}>Solo la mia voce</MaskLine>
      <MaskLine delay={0.2} className="text-outline">e troppe notti insonni.</MaskLine>
    </h2>

    <div className="mt-16 grid gap-12 lg:mt-24 lg:grid-cols-12 lg:gap-16">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-24">
          <Portrait />
          <div className="mt-6 grid grid-cols-3 border-t border-line">
            {STATS.map((s) => (
              <div key={s.l} className="border-r border-line py-5 pr-3 last:border-r-0 [&:not(:first-child)]:pl-4">
                <p className="font-display text-3xl font-black text-acid sm:text-4xl">{s.v}</p>
                <p className="mt-2 font-mono text-[10px] uppercase leading-snug tracking-[0.15em] text-smoke">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-7">
        {CHAPTERS.map((c, i) => (
          <motion.article
            key={c.n}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10% 0px" }}
            transition={{ duration: 1, delay: i * 0.05, ease: EASE }}
            className="grid grid-cols-[auto_1fr] gap-6 border-t border-line py-10 sm:gap-10"
          >
            <span className="font-display text-5xl font-black text-outline-soft sm:text-7xl">{c.n}</span>
            <div>
              <h3 className="font-display text-xl font-bold uppercase tracking-tight sm:text-2xl">{c.title}</h3>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-bone/70 sm:text-lg">{c.text}</p>
            </div>
          </motion.article>
        ))}

        <div className="mt-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">
          <Equalizer /> In ascolto ora su <span className="text-bone">Spotify</span>
        </div>
        <div className="mt-6">
          <SpotifyCard />
        </div>
      </div>
    </div>

    <StudioGallery />
  </section>
);
